import React from "react";
import { Theme, ThemeBackgroundType, ThemeFontVariant, ThemeIntent } from "../types";
import { BemHelper } from "@/utils/BemHelper";
import classNames from "classnames";
import {
  FaFacebookF,
  FaInstagram,
  FaLink,
  FaLinkedinIn,
  FaPinterest,
  FaTiktok,
  FaTwitter,
  FaYoutube,
} from "react-icons/fa";
import { THEMES, THEME_BACKGROUND_TYPES, THEME_FONT_VARIANTS, THEME_INTENTS } from "../constants";
import { useActiveBreakpoints } from "@/utils/useActiveBreakpoints";
import { isLightTheme } from "../utils";

export type CtSocialMediaCardIcons =
  | "instagram"
  | "tiktok"
  | "linkedin"
  | "twitter"
  | "facebook"
  | "pinterest"
  | "youtube"
  | "website_1"
  | "website_2"
  | "website_3";

export type CtSocialMediaCardProps = {
  theme?: Theme;
  intent?: ThemeIntent;
  backgroundType?: ThemeBackgroundType;
  fontVariant?: ThemeFontVariant;
  className?: string;
  numberOfBookings?: number;
  hideNumberOfBookings?: boolean;
  disablePaddings?: boolean;
  socialMediaLinks: Record<CtSocialMediaCardIcons, string | undefined | null>;
  hideSocialMediaLinks?: boolean;
};

const icons: Record<CtSocialMediaCardIcons, React.FC> = {
  instagram: FaInstagram,
  tiktok: FaTiktok,
  linkedin: FaLinkedinIn,
  twitter: FaTwitter,
  facebook: FaFacebookF,
  pinterest: FaPinterest,
  youtube: FaYoutube,
  website_1: FaLink,
  website_2: FaLink,
  website_3: FaLink,
};

const bem = new BemHelper("ct-social-media-card");

function CtSocialMediaCard({
  intent,
  theme,
  fontVariant,
  className,
  numberOfBookings,
  hideNumberOfBookings,
  socialMediaLinks,
  backgroundType,
  disablePaddings,
  hideSocialMediaLinks,
}: CtSocialMediaCardProps) {
  const { activeBreakpoints } = useActiveBreakpoints();

  const isLightThemeAndDarkBackground =
    isLightTheme(theme) && backgroundType === THEME_BACKGROUND_TYPES.DARK;

  return (
    <div
      className={classNames(
        bem.block(),
        className,
        bem.modifier(!theme || isLightThemeAndDarkBackground ? THEMES.DEFAULT : theme),
        bem.modifier(
          !backgroundType || isLightThemeAndDarkBackground
            ? THEME_BACKGROUND_TYPES.LIGHT
            : backgroundType,
        ),
        bem.modifier(fontVariant || THEME_FONT_VARIANTS.FONT_VARIANT_1),
        bem.modifier(intent || THEME_INTENTS.DARK),
        disablePaddings && bem.modifier("disable-paddings"),
      )}
    >
      {!hideNumberOfBookings && (
        <div
          className={classNames(
            bem.element("bookings"),
            !activeBreakpoints.md && bem.modifier("mobile", "bookings"),
          )}
        >
          Bookings{" "}
          <span className={classNames(bem.element("number-of-bookings"))}>{numberOfBookings}</span>
        </div>
      )}
      {!hideSocialMediaLinks && (
        <div className={classNames(bem.element("icons"))}>
          {Object.keys(socialMediaLinks).map((key: CtSocialMediaCardIcons) => {
            const Icon = icons[key];

            if (!socialMediaLinks[key] || !Icon) {
              return null;
            }

            return (
              <a
                key={key}
                href={socialMediaLinks[key] as string}
                target='_blank'
                rel='noopener noreferrer'
                className={classNames(
                  bem.element("icon"),
                  !activeBreakpoints.md && bem.modifier("mobile", "icon"),
                )}
              >
                <Icon />
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}

CtSocialMediaCard.defaultProps = {
  theme: THEMES.DEFAULT,
  fontVariant: THEME_FONT_VARIANTS.FONT_VARIANT_1,
  classNames: undefined,
  backgroundType: THEME_BACKGROUND_TYPES.LIGHT,
  intent: THEME_INTENTS.DARK,
  numberOfBookings: 0,
  hideNumberOfBookings: false,
  socialMediaLinks: {},
  disablePaddings: false,
  hideSocialMediaLinks: false,
};

export default CtSocialMediaCard;
